import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
} from "@elaniintc/goes-design-system";
import type { IDaySelected } from "../..";
import { formatLongDate, type EventForDay } from "../utils/utils";

export interface DayModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleSubmit: () => void;
  selectedDay: IDaySelected;
  handleEditEvent: (event: EventForDay) => void;
}

const DayModal = ({
  isOpen,
  onClose,
  selectedDay,
  handleSubmit,
  handleEditEvent,
}: DayModalProps) => {
  const { hasPermission } = useAuthorization();

  const isAvailable = selectedDay.isAvailable && !selectedDay.hasEventBlocked;
  const canUpdate = hasPermission(Permissions["calendar"]["update"]);
  const canCreate = hasPermission(Permissions["calendar"]["create"]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        showCloseButton={false}
        type="side"
        overlay={false}
        className="max-w-110 max-h-[calc(100vh-78px)] mt-17.25 mr-3  bg-neutral-foreground border-l border-neutral-quiet rounded-none p-0"
      >
        <div className="w-full h-full flex flex-col gap-1.5">
          <DialogHeader className="px-4 p-4 bg-neutral-object border-b border-neutral-silent flex flex-row items-center justify-between">
            <div className="flex gap-2 items-center">
              <Avatar size="sm" shape="square">
                <AvatarFallback shape="square" type="icon">
                  <Icon
                    type="fas"
                    name={isAvailable ? "calendar" : "calendar-xmark"}
                    size="lg"
                  />
                </AvatarFallback>
              </Avatar>
              <div>
                <DialogTitle className="text-h6 font-semibold text-heading">
                  {formatLongDate(selectedDay.date)}
                </DialogTitle>
                <div className="flex gap-2">
                  <span className="text-heading-quiet">
                    {selectedDay.isAvailable
                      ? "Día de semana"
                      : "Fin de semana"}
                  </span>
                  <span className="text-on-disabled">|</span>
                  <span
                    className={cn(
                      isAvailable ? "text-action" : "text-on-disabled",
                    )}
                  >
                    {isAvailable ? "Hábil" : "No hábil"}
                  </span>
                </div>
              </div>
            </div>
            <Button
              size="icon"
              variant="quiet"
              aria-label="Cerrar selección de nodo"
              type="button"
              onClick={onClose}
              data-testid="close-day-modal"
            >
              <Icon type="far" name="times" />
            </Button>
          </DialogHeader>

          <div className={cn("flex-1 min-h-0 flex flex-col overflow-y-auto ")}>
            <div
              className={cn(
                "flex flex-col flex-1 px-2",
                selectedDay.events.length === 0 && "justify-center",
              )}
            >
              <div className="flex flex-col h-full gap-2">
                {selectedDay.events.length > 0 ? (
                  selectedDay.events.map((event) => {
                    const isBlocked = [
                      "HOLIDAY",
                      "NON_WORKING_DAY",
                      "WEEKEND",
                    ].includes(event.type);

                    return (
                      <div
                        key={event.id}
                        className={cn(
                          " pl-2 rounded-lg ",
                          isBlocked
                            ? "bg-surface-warning-solid border border-surface-warning-solid"
                            : "bg-surface-info-solid border border-surface-info-solid",
                        )}
                      >
                        <div className="rounded-r-lg flex bg-neutral-object items-stretch">
                          <div className="p-2 flex-1">
                            <div className="flex gap-2 items-center">
                              <div className="flex gap-2 items-center flex-1">
                                <Icon
                                  type="fa"
                                  name={
                                    isBlocked ? "calendar-xmark" : "calendar"
                                  }
                                  className={cn(
                                    isBlocked ? "text-warning" : "text-info",
                                  )}
                                />
                                <span className="text-body-loud text-lg font-bold">
                                  {event.name}
                                </span>
                              </div>

                              {canUpdate && (
                                <Button
                                  aria-label="edit-event-button"
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditEvent(event)}
                                  data-testId="edit-event-button"
                                >
                                  <Icon
                                    type="fa"
                                    name="edit"
                                    className={cn(
                                      isBlocked ? "text-warning" : "text-info",
                                    )}
                                    size="lg"
                                  />
                                </Button>
                              )}
                            </div>

                            <div className="text-sm text-body flex gap-2 items-center">
                              <span>{event.start_date}</span>
                              <Icon type="fa" name="arrow-right" size="sm" />
                              <span>{event.end_date}</span>
                            </div>

                            <span className="text-body-quiet text-sm">
                              Creado por:{" "}
                              <span className="text-action">
                                {event?.createdBy?.name ?? ""}{" "}
                                {event?.createdBy?.last_name ?? ""}
                              </span>
                            </span>
                          </div>

                          {event.dayLabel !== "1/1" && (
                            <div
                              className={cn(
                                "rounded-r-lg px-2 flex items-center",
                                isBlocked
                                  ? "text-warning bg-surface-warning-accent-click"
                                  : "text-info bg-surface-info-accent-click",
                              )}
                            >
                              <span className="font-bold">
                                {event.dayLabel}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="h-full flex flex-col justify-center items-center">
                    <Heading variant="h6" className="text-on-disabled">
                      Día sin eventos programados
                    </Heading>
                    <span className="text-on-disabled text-center text-sm">
                      Este día no tiene ningún evento <br /> programado
                      actualmente
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {canCreate && (
            <DialogFooter className="px-4 p-4 border-t border-neutral-silent bg-neutral-object">
              <div className="w-full flex justify-start  py-3 gap-3">
                <Button
                  size="xs"
                  variant="tertiary"
                  data-testid="add-event-day"
                  onClick={handleSubmit}
                >
                  <span className="hidden md:inline-block">Añadir Evento</span>
                  <Icon type="far" name="plus" className="text-current" />
                </Button>
              </div>
            </DialogFooter>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DayModal;
